/**
 * Språkkonfiguration per sökväg.
 *
 * autofrance.se       → svenska, SEK
 * autofrance.se/en/   → engelska, SEK
 *
 * Fram till 2026-09-02 låg engelskan på autofrance.me med EUR, 10 % påslag och
 * moms 0 (export till Montenegro). Den domänen 301:as numera till autofrance.se
 * och engelskan bor under /en/. Språk och handelsvillkor är därmed frikopplade:
 * en engelsktalande kund i Sverige ska ha SEK och 25 % moms precis som alla
 * andra. Momsfri export hanteras i företagsflödet vid kassan, inte per språk.
 */

export type Locale = 'sv' | 'en';
export type Currency = 'SEK' | 'EUR';

export interface SiteConfig {
  locale: Locale;
  currency: Currency;
  domain: string;
  countryCode: string;
  /** Momssats (0.25 = 25 % svensk moms) */
  vatRate: number;
  /**
   * Prispåslag (1.0 = inget påslag).
   *
   * Alltid 1.0 sedan 2026-09-02. Fältet är kvar därför att sexton filer läser
   * det i grenar villkorade av `currency === 'EUR'`, som aldrig inträffar.
   * Se ARCHITECTURE.md, avsnitt F.
   */
  priceMarkup: number;
  htmlLang: string;
  siteName: string;
}

export const SITE_URL = 'https://autofrance.se';

/** Sökvägsprefixet för engelska. Svenska har inget prefix. */
export const EN_PREFIX = '/en';

const SE_CONFIG: SiteConfig = {
  locale: 'sv',
  currency: 'SEK',
  domain: 'autofrance.se',
  countryCode: 'SE',
  vatRate: 0.25,
  priceMarkup: 1.0,
  htmlLang: 'sv',
  siteName: 'Autofrance',
};

/** Engelska ärver allt utom språket — samma valuta, moms och villkor. */
const EN_CONFIG: SiteConfig = {
  ...SE_CONFIG,
  locale: 'en',
  htmlLang: 'en',
};

const CONFIGS: Record<Locale, SiteConfig> = { sv: SE_CONFIG, en: EN_CONFIG };

/**
 * Sidtyper som ingår i den engelska första omgången.
 *
 * Allt annat under /en/ saknar route och får därmed Next.js 404 av sig självt —
 * det är avsiktligt. Kassa, konto, varukorg och företagsportalen ska INTE svara
 * på engelska: de är oöversatta och ett halvöversatt köpflöde är värre än ett
 * svenskt. Vill du utöka omgången är det den här listan du rör, inte proxy.ts.
 */
const EN_ALLOWED_PREFIXES = [
  '/katalog',
  // /sok är med trots att den inte ska rankas: sökrutan sitter i headern på varje
  // engelsk sida, så utan den kastas besökaren ut ur /en/-trädet mitt i sessionen.
  // Den ärver svenskans robots-regel (noindex utan query och på sida 2+) och ligger
  // medvetet utanför sitemapen. Ska fungera, inte rankas.
  '/sok',
  '/produkt',
  '/reservdelar',
  '/faq',
  '/om-oss',
  '/kontakt',
  '/frakt',
  '/retur',
  '/villkor',
];

/** Är sökvägen (utan språkprefix) med i den engelska omgången? */
export function isEnglishRoute(pathname: string): boolean {
  if (pathname === '/') return true;
  return EN_ALLOWED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`) || pathname.startsWith(`${p}-`),
  );
}

/** Språket som sökvägen anger. Allt utan /en-prefix är svenska. */
export function localeFromPath(pathname: string): Locale {
  return pathname === EN_PREFIX || pathname.startsWith(`${EN_PREFIX}/`) ? 'en' : 'sv';
}

/**
 * Dela upp en sökväg i språk + sökväg utan prefix.
 * `/en/katalog` → `{ locale: 'en', pathname: '/katalog' }`
 * `/katalog`    → `{ locale: 'sv', pathname: '/katalog' }`
 */
export function stripLocalePrefix(pathname: string): { locale: Locale; pathname: string } {
  if (pathname === EN_PREFIX) return { locale: 'en', pathname: '/' };
  if (pathname.startsWith(`${EN_PREFIX}/`)) {
    return { locale: 'en', pathname: pathname.slice(EN_PREFIX.length) || '/' };
  }
  return { locale: 'sv', pathname };
}

/**
 * Lägg på språkprefix på en intern sökväg.
 *
 * Detta är den enda funktion som får bygga prefixade länkar. Använd den inte
 * direkt i komponenter — gå via <Link> i components/LocaleLink.tsx, som läser
 * språket ur kontexten åt dig.
 */
export function withLocalePrefix(pathname: string, locale: Locale): string {
  if (locale !== 'en') return pathname;
  if (!pathname.startsWith('/')) return pathname; // extern URL, ankare, mailto:
  if (pathname === EN_PREFIX || pathname.startsWith(`${EN_PREFIX}/`)) return pathname; // idempotent
  // Frågesträng och fragment ingår inte i routen. Utan den här delningen matchar
  // t.ex. '/sok?q=broms' inte '/sok' och sökningen tappar sitt /en-prefix.
  const [bare] = pathname.split(/[?#]/);
  if (!isEnglishRoute(bare || '/')) return pathname; // oöversatt sida — länka till svenska
  return bare === '/' ? `${EN_PREFIX}${pathname.slice(1)}` : `${EN_PREFIX}${pathname}`;
}

/** Konfigurationen för ett språk. */
export function getSiteConfig(locale: Locale): SiteConfig {
  return CONFIGS[locale] ?? SE_CONFIG;
}
